"use client";

import { resolveUnknown } from "./binding";
import { resolveBool } from "./binding";
import { MacaronButtonFillContext, MacaronButtonInnerContext } from "./scope";
import { MACARON } from "./tokens";
import type { MacaronRenderContext, RecordAny } from "./types";
import { useContext } from "react";

function resolveActionContext(action: RecordAny | undefined, data: RecordAny): Record<string, unknown> | null {
  const contextEntries = Array.isArray(action?.context) ? action.context : [];
  const context: Record<string, unknown> = {};
  for (const entry of contextEntries) {
    if (!entry || typeof entry !== "object" || typeof (entry as RecordAny).key !== "string" || !(entry as RecordAny).key) continue;
    context[(entry as RecordAny).key as string] = resolveUnknown((entry as RecordAny).value, data);
  }
  return Object.keys(context).length > 0 ? context : null;
}

export function MacaronButton({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const child = typeof config.child === "string" ? config.child : "";
  const style = (typeof config.style === "string" ? config.style : "primary") as "primary" | "secondary" | "plain";
  const action = config.action as RecordAny | undefined;
  const localHandler = action?.handler && typeof action.handler === "object" ? (action.handler as RecordAny) : undefined;
  const localHandlerOperation = typeof localHandler?.operation === "string" ? localHandler.operation : "";
  const actionName = typeof action?.name === "string" ? action.name : "";
  const enabled = resolveBool(config.enabled, ctx.data);
  const shouldFillWidth = useContext(MacaronButtonFillContext);

  const base =
    `inline-flex max-w-full ${shouldFillWidth ? "w-full" : "w-auto"} cursor-pointer items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-opacity disabled:cursor-not-allowed`;
  const visual =
    style === "secondary"
      ? "ring-1 ring-inset ring-[#DAD8D3] bg-white text-[#344054]"
      : style === "plain"
        ? "bg-transparent text-[#344054] underline-offset-2 hover:underline"
        : "text-white [&_div]:text-inherit [&_span]:text-inherit";

  const gradient =
    style === "primary"
      ? {
          background: `linear-gradient(135deg, ${MACARON.buttonPrimaryStart}, ${MACARON.buttonPrimaryMid}, ${MACARON.buttonPrimaryEnd})`,
        }
      : undefined;

  return (
    <button
      type="button"
      className={`${base} ${visual}`}
      style={{ ...gradient, opacity: enabled ? 1 : 0.4 }}
      disabled={!enabled}
      onClick={() => {
        if (!enabled) return;
        if (localHandlerOperation) {
          ctx.onLocalHandler?.(localHandlerOperation, ctx.componentId, localHandler?.value);
          return;
        }
        if (!actionName) return;
        ctx.onAction?.(actionName, ctx.componentId, resolveActionContext(action, ctx.data));
      }}
    >
      <MacaronButtonInnerContext.Provider value={true}>{child ? ctx.renderChild(child) : null}</MacaronButtonInnerContext.Provider>
    </button>
  );
}
